import classNames from 'classnames/bind';
import PropTypes from 'prop-types';
import { Carousel } from 'react-responsive-carousel';
import 'react-responsive-carousel/lib/styles/carousel.min.css';
import { Link } from 'react-router-dom';
import { sanitize } from 'isomorphic-dompurify';
import styles from './styles.module.scss';
import { Button, LinkToRentals } from '../../../components';
import { dashSpaceSwap } from '../../../utils/url-search-helpers';
import { StaticUrls } from '../../../shared/static-urls-enum';
import { DiscoverActions } from '../../../zustand-stores/DiscoverStore';

const cx = classNames.bind(styles);

const Hero = ({ cityName, heroSlides, slideDuration }) => {
  const dynamicCarousel = heroSlides.length
    ? heroSlides.map(heroSlide => ({
        title: heroSlide.slideText,
        description: heroSlide.slideSubText,
        action: (
          <>
            <a
              href={heroSlide.slideLink}
              target={heroSlide.newTab ? '_blank' : '_self'}
              rel="noreferrer"
            >
              <Button className={cx('ctaButton')}>{heroSlide.slideButtonText}</Button>
            </a>
            {heroSlide.secondarySlideButtonText && (
              <a
                href={heroSlide.secondarySlideLink}
                target={heroSlide.secondaryNewTab ? '_blank' : '_self'}
                rel="noreferrer"
              >
                <Button className={cx('ctaButton', 'ml-2')}>
                  {heroSlide.secondarySlideButtonText}
                </Button>
              </a>
            )}
          </>
        ),
        slideImage: heroSlide.slideImage,
      }))
    : [];

  const searchCityParams = cityName ? `&cityName=${dashSpaceSwap(cityName, 'toSpace')}` : '';

  const handleClick = () => {
    // Ensure the city is cleared from global discover state if no city is selected on the current page.
    if (!cityName) DiscoverActions.updateFilters({ cityName: null });
  };

  return (
    <div className={cx('heroContainer')}>
      <Carousel
        infiniteLoop
        autoPlay
        interval={slideDuration || 5000}
        stopOnHover
        showThumbs={false}
        className={cx('carousel')}
      >
        {dynamicCarousel.map(({ title, description, action, slideImage }) => (
          <div className={cx('hero')} style={{ backgroundImage: `url(${slideImage})` }} key={title}>
            <div className={cx('jumbotron')}>
              <h1 className={cx('title')}>{title}</h1>
              <div
                className={cx('subtitle')}
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{ __html: sanitize(description) }}
              />
              <div className="hero-action">{action}</div>
            </div>
          </div>
        ))}
      </Carousel>
      <div className="container mt-5">
        <div className={cx('heroButtonContainer')}>
          <Link
            onClick={handleClick}
            to={{
              pathname: '/discover',
              search: `${searchCityParams}&subView=DAILY&view=SPORTS`,
            }}
          >
            <Button skyblue>Play Today</Button>
          </Link>
          <Link
            onClick={handleClick}
            to={{
              pathname: '/discover',
              search: `${searchCityParams}&subView=LEAGUE&view=SPORTS`,
            }}
          >
            <Button skyblue>Join a League</Button>
          </Link>
          <a href={StaticUrls.VOLOKIDS_VOLUNTEER} target="_blank" rel="noopener noreferrer">
            <Button skyblue>Volunteer</Button>
          </a>
          <LinkToRentals cityName={cityName}>
            <Button skyblue>NEW - Court Rentals</Button>
          </LinkToRentals>
        </div>
      </div>
    </div>
  );
};

Hero.propTypes = {
  cityName: PropTypes.string.isRequired,
  heroSlides: PropTypes.arrayOf(
    PropTypes.shape({
      slideImage: PropTypes.string,
      slideText: PropTypes.string,
      slideSubText: PropTypes.string,
      slideButtonText: PropTypes.string,
      slideLink: PropTypes.string,
      newTab: PropTypes.bool,
      secondarySlideButtonText: PropTypes.string,
      secondarySlideLink: PropTypes.string,
      secondaryNewTab: PropTypes.bool,
    })
  ).isRequired,
  slideDuration: PropTypes.number.isRequired,
};

export default Hero;
